import type { RoadRecord } from './types'
import type { RouteTrims } from './trim'
import { trimmedWaypoints, metersBetween, polylineMeters } from './trim'
import { occurrenceKey } from './store'

// Build a single Google Maps directions URL covering every road in the route.
// api.ts is the data layer (it loads the sheet + resolves geometry) and must
// not import this pure-helper module, so the waypoint extractor is loaded
// lazily — keeps the dependency direction one-way and avoids a circular import.
// Each road contributes its own sheet link's start/end waypoints; roads whose
// link is a single point (or missing) contribute that point only. Points are
// emitted in route order — Google respects order for /dir/ multi-stop URLs.
// Endpoint-direction ordering: a road entered in reverse (its link runs B→A
// while the rider travels A→B) contributes its waypoints reversed, so Google
// Directions doesn't add wasteful U-turns between roads.
// Passing no origin makes Google Maps default the first leg to the rider's
// "Your Location" — used for the route's opening link.
function buildDirUrl(origin: string | null, destination: string, waypoints: string[]): string {
  const wp = waypoints.join('|')
  const params = new URLSearchParams({
    api: '1',
    destination,
    ...(wp ? { waypoints: wp } : {}),
  })
  if (origin) params.set('origin', origin)
  return `https://www.google.com/maps/dir/?${params.toString()}`
}

// Build Google Maps directions URLs covering every road in the route. Google
// Maps accepts at most 10 locations per directions link (origin + destination
// + 8 waypoints), so routes with more points are split into sequential links —
// each link starts where the previous one ended so the ride continues seamlessly.
// Ordered [lng, lat][] waypoint chain covering the whole route — shared by
// the Google Maps links and the route-summary totals so both describe the
// exact same connected ride.
// Per-road waypoint chains in route order — the flat route chain is their
// concatenation, and the boundary between consecutive chains is exactly what
// the save/share layout check inspects.
async function buildRouteChains(
  routeRoads: RoadRecord[],
  trims?: RouteTrims,
): Promise<[number, number][][]> {
  const { extractLinkWaypoints } = await import('./api')
  const chains: [number, number][][] = []
  const fixed: boolean[] = []
  // A road can appear several times, each visit trimmed differently — the
  // trim key is per occurrence (1st pass = plain id, 2nd = "id#2"…).
  const seen = new Map<string, number>()
  for (const road of routeRoads) {
    const pass = seen.get(road.id) ?? 0
    seen.set(road.id, pass + 1)
    const link = road.google_maps_url
    if (!link) continue
    let wps = extractLinkWaypoints(link)
    if (wps.length === 0) continue
    // Trim in the link's own (canonical) orientation FIRST: the cuts are
    // positional along the road, so they only mean anything before any
    // direction flipping. Flipping the chain first (the old behavior) made
    // the single-cut defaults keep the wrong half — emitting waypoints from
    // the trimmed-off portion. trimmedWaypoints replaces the endpoint(s)
    // with the cut coordinate(s) so the route leaves/enters the road exactly
    // at the cut, keeps original waypoints inside the kept section, and
    // applies an explicit reversal by flipping the trimmed chain itself.
    const trim = trims?.[occurrenceKey(road.id, pass)]
    if (trim) wps = trimmedWaypoints(wps, trim)
    chains.push(wps)
    // An explicit reversal is the user's stated travel direction — fixed.
    fixed.push(!!trim?.reversed)
  }
  optimizeChainOrientations(chains, fixed)
  return chains
}

// Choose each chain's direction so consecutive roads connect as closely as
// possible. Route order is never changed — chains are only flipped
// end-for-end, and chains the user explicitly reversed stay fixed. A greedy
// left-to-right pass loses here: the FIRST road's direction must serve the
// second (with no previous road to anchor it, the old per-road guess left it
// facing the wrong way and every later road flipped to chase it). Instead we
// run coordinate-descent passes — each free road flips only if that strictly
// shrinks its two connection gaps — until a full pass changes nothing.
const ORIENT_PASSES = 5

function optimizeChainOrientations(chains: [number, number][][], fixed: boolean[]): void {
  if (chains.length < 2) return
  for (let pass = 0; pass < ORIENT_PASSES; pass++) {
    let changed = false
    for (let i = 0; i < chains.length; i++) {
      if (fixed[i] || chains[i].length < 2) continue
      const cur = chains[i]
      const rev = [...cur].reverse()
      const cost = (c: [number, number][]) => {
        let s = 0
        const left = chains[i - 1]
        if (left?.length) s += metersBetween(left[left.length - 1], c[0])
        const right = chains[i + 1]
        if (right?.length) s += metersBetween(c[c.length - 1], right[0])
        return s
      }
      if (cost(rev) + 1 < cost(cur)) {
        chains[i] = rev
        changed = true
      }
    }
    if (!changed) break
  }
}

// Two waypoints closer than this read as the same place (a trim endpoint
// snapped to an intersecting road's own waypoint) — the export keeps one.
const DEDUPE_METERS = 0.2 * 1609.344

export async function buildRouteWaypoints(
  routeRoads: RoadRecord[],
  trims?: RouteTrims,
): Promise<[number, number][]> {
  const pts = (await buildRouteChains(routeRoads, trims)).flat()
  // Collapse consecutive duplicates: keep the first point of each cluster
  // and drop any following point within 0.2 mi of the last kept one.
  const out: [number, number][] = []
  for (const pt of pts) {
    const last = out[out.length - 1]
    if (last && metersBetween(last, pt) < DEDUPE_METERS) continue
    out.push(pt)
  }
  return out
}

export interface RouteLayoutIssue {
  // The gap sits between this road and the next one in the route.
  afterRoadIndex: number
  gapMiles: number
}

// Save/share sanity check: consecutive roads' waypoint chains must meet.
// A big jump between two chains means a road runs the wrong way (an
// accidental Reverse) or sits out of order — exactly the kind of mistake
// that produces a nonsensical Google Maps route. Connector gaps up to
// ~1 mile (or 25% of the shorter road, for short roads) are normal.
export async function findRouteLayoutIssues(
  routeRoads: RoadRecord[],
  trims?: RouteTrims,
): Promise<RouteLayoutIssue[]> {
  const chains = await buildRouteChains(routeRoads, trims)
  const issues: RouteLayoutIssue[] = []
  for (let i = 0; i < chains.length - 1; i++) {
    const a = chains[i]
    const b = chains[i + 1]
    if (!a.length || !b.length) continue
    const gapM = metersBetween(a[a.length - 1], b[0])
    if (gapM <= 1609) continue
    // Only suspect a reversal when flipping one of the two neighbouring
    // chains end-for-end would actually CLOSE the gap — i.e. the alternate
    // endpoint of each chain is a real, strictly better connection. If no
    // reversed pairing helps, this is just two roads that legitimately
    // don't touch (the rider rides between them), so never flag it.
    const altA = a[0]
    const altB = b[b.length - 1]
    const closesA = metersBetween(altA, b[0]) < gapM - 805
    const closesB = metersBetween(a[a.length - 1], altB) < gapM - 805
    if (!closesA && !closesB) continue
    const shorterM = Math.min(polylineMeters(a), polylineMeters(b))
    issues.push({
      afterRoadIndex: i,
      gapMiles: Math.round((gapM / 1609.344) * 10) / 10,
    })
  }
  return issues
}

export async function buildRouteMapsUrls(
  routeRoads: RoadRecord[],
  trims?: RouteTrims,
): Promise<string[]> {
  const pts = await buildRouteWaypoints(routeRoads, trims)
  if (pts.length === 0) return []
  const fmt = ([lng, lat]: [number, number]) => `${lat.toFixed(6)},${lng.toFixed(6)}`
  const urls: string[] = []
  // Google Maps directions links accept at most 10 locations (origin +
  // waypoints + destination). The opening link spends one slot on the
  // rider's "Your Location" (omitting the origin makes Google use it) —
  // but keeps the route's real first waypoint as the first stop, so only
  // 9 route points fit. Later links re-use the previous destination as
  // their origin and carry 10 route points each.
  const MAX_LOCATIONS = 10
  let start = 0
  let first = true
  while (start < pts.length - 1) {
    const end = Math.min(start + (first ? MAX_LOCATIONS - 2 : MAX_LOCATIONS - 1), pts.length - 1)
    const waypoints = (first ? pts.slice(start, end) : pts.slice(start + 1, end)).map(fmt)
    urls.push(buildDirUrl(first ? null : fmt(pts[start]), fmt(pts[end]), waypoints))
    first = false
    start = end
  }
  return urls
}

export interface GeneratedRide {
  roadIds: string[]
  roads: RoadRecord[]
  totalMiles: number
  avgRating: number
  estHours: number
}

export type RideLength = '1h' | '2h' | 'half' | 'full'
export type RideStyle = 'twisty' | 'scenic' | 'adventure' | 'mixed'

const TARGET_MILES: Record<RideLength, number> = { '1h': 35, '2h': 70, half: 150, full: 250 }

const FREEWAY_RE = /\b(I-\d|US-\d)/i

function mulberry32(seed: number) {
  let a = seed | 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function shuffle<T>(arr: T[], rnd: () => number): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function styleMatches(r: RoadRecord, style: RideStyle): boolean {
  const rt = r.ratings ?? ({} as RoadRecord['ratings'])
  switch (style) {
    case 'twisty':
      return (rt.fun ?? 0) >= 8.5
    case 'scenic':
      return (rt.pavement ?? 0) >= 8.5 && (rt.traffic ?? 0) >= 8
    case 'adventure':
      return r.bike_types.includes('ADV') || !r.fully_paved
    case 'mixed':
      return (rt.overall ?? 0) >= 8
  }
}

function roadMiles(r: RoadRecord): number {
  return r.distance_miles ?? 15
}

function buildRide(picked: RoadRecord[]): GeneratedRide {
  const totalMiles = Math.round(picked.reduce((s, r) => s + roadMiles(r), 0))
  const rated = picked.map((r) => r.ratings?.overall).filter((v): v is number => v != null)
  const avgRating = rated.length
    ? Math.round((rated.reduce((s, v) => s + v, 0) / rated.length) * 10) / 10
    : 0
  return {
    roadIds: picked.map((r) => r.id),
    roads: picked,
    totalMiles,
    avgRating,
    estHours: Math.round((totalMiles / 42) * 10) / 10,
  }
}

export function generateRide(
  roads: RoadRecord[],
  opts: { length: RideLength; style: RideStyle; avoidFreeways: boolean; seed?: number },
): GeneratedRide | null {
  if (!roads.length) return null

  const target = TARGET_MILES[opts.length]
  const rnd = mulberry32(opts.seed ?? 1)

  let candidates = roads.filter((r) => {
    if (opts.avoidFreeways && FREEWAY_RE.test(r.name)) return false
    return styleMatches(r, opts.style)
  })

  const pickFrom = (pool: RoadRecord[]): RoadRecord[] => {
    const shuffled = shuffle(pool, rnd)
    const sorted = [...shuffled].sort(
      (a, b) => (b.ratings?.overall ?? 0) - (a.ratings?.overall ?? 0),
    )
    const picked: RoadRecord[] = []
    let miles = 0
    for (const r of sorted) {
      const m = roadMiles(r)
      if (miles >= target * 0.75) break
      if (miles + m > target * 1.25) continue
      picked.push(r)
      miles += m
    }
    return picked
  }

  if (candidates.length >= 2) {
    const groups = new Map<string, RoadRecord[]>()
    for (const r of candidates) {
      const key = `${r.region}||${r.group}`
      const list = groups.get(key) ?? []
      list.push(r)
      groups.set(key, list)
    }
    let best: RoadRecord[] = []
    let bestCount = -1
    let bestAvg = -1
    for (const list of groups.values()) {
      const avg =
        list.reduce((s, r) => s + (r.ratings?.overall ?? 0), 0) / Math.max(list.length, 1)
      if (list.length > bestCount || (list.length === bestCount && avg > bestAvg)) {
        best = list
        bestCount = list.length
        bestAvg = avg
      }
    }
    const picked = pickFrom(best)
    if (picked.length >= 2) {
      const ride = buildRide(picked)
      return { ...ride, estHours: Math.round(ride.estHours * (opts.style === 'twisty' ? 1.15 : 1) * 10) / 10 }
    }
  }

  // Relax: fall back to top-rated roads anywhere
  const relaxed = roads.filter((r) => !(opts.avoidFreeways && FREEWAY_RE.test(r.name)))
  const picked = pickFrom(relaxed.length >= 2 ? relaxed : roads)
  if (!picked.length) return null
  const ride = buildRide(picked)
  return {
    ...ride,
    estHours: Math.round(ride.estHours * (opts.style === 'twisty' ? 1.15 : 1) * 10) / 10,
  }
}
